import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export interface UserProfile {
  currency: string;   // ISO 4217, e.g. 'BRL', 'USD', 'EUR'
  timezone: string;   // IANA, e.g. 'America/Sao_Paulo'
  full_name: string;
}

const DEFAULTS: UserProfile = {
  currency: 'BRL',
  timezone: 'America/Sao_Paulo',
  full_name: '',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export function useUserProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    db.from('user_profiles')
      .select('currency, timezone, full_name')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }: { data: Partial<UserProfile> | null; error: unknown }) => {
        if (cancelled) return;
        if (error) console.error('[useUserProfile] fetch error:', error);
        if (data) {
          setProfile({
            currency: data.currency || DEFAULTS.currency,
            timezone: data.timezone || DEFAULTS.timezone,
            full_name: data.full_name || DEFAULTS.full_name,
          });
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user?.id]);

  const update = async (patch: Partial<UserProfile>) => {
    if (!user) return;
    const next = { ...profile, ...patch };
    setProfile(next); // optimistic

    const { error } = await db
      .from('user_profiles')
      .upsert(
        { user_id: user.id, ...patch },
        { onConflict: 'user_id' }
      );

    if (error) {
      console.error('[useUserProfile] update error:', error);
      setProfile(profile); // rollback
    }
  };

  return { profile, loading, update };
}
